import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { ShieldAlert, ChevronRight, ChevronLeft, Calculator, Save, Printer, Trash2, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';

// ── Constants ──────────────────────────────────────────────
const CHEMICAL_TYPES = [
  { value: 'toxic_gas',      label: 'Toxic Gas',                    icon: '☁️', desc: 'Gas at ambient conditions or liquefied under pressure (e.g., chlorine, ammonia, phosgene, sulfur dioxide)' },
  { value: 'toxic_liquid',   label: 'Toxic Liquid',                 icon: '💧', desc: 'Liquid at ambient conditions (e.g., bromine, methyl isocyanate, cyclohexylamine)' },
  { value: 'aqueous_solution',label:'Aqueous Solution (Toxic)',     icon: '🧪', desc: 'Water solutions of toxic substances (e.g., aqueous ammonia, hydrochloric acid, hydrofluoric acid)' },
  { value: 'flammable_gas',  label: 'Flammable Gas / Flammable Liquid Under Pressure', icon: '🔥', desc: 'Flammable gases or liquids under pressure (e.g., propane, methane, dimethylamine)' },
  { value: 'flammable_liquid',label:'Flammable Liquid',             icon: '⛽', desc: 'Flammable liquids at atmospheric pressure (e.g., ethyl ether, isopropylamine)' },
];

const SCENARIO_TYPES = [
  { value: 'worst_case',   label: 'Worst-Case Release Scenario',   desc: 'Largest single vessel or pipeline failure. Required for all regulated processes. Wind: 1.5 m/s, F stability.' },
  { value: 'alternative',  label: 'Alternative Release Scenario',   desc: 'More likely to occur than worst-case. At least one per toxic substance. Wind: 3 m/s, D stability.' },
];

const SITE_TYPES = [
  { value: 'rural', label: 'Rural — flat terrain, no buildings nearby' },
  { value: 'urban', label: 'Urban — buildings or trees in immediate area' },
];

const RELEASE_SCENARIOS = [
  { value: 'vce',       label: 'Vapor Cloud Explosion (VCE)', desc: '1 psi overpressure endpoint — use for gases or flammable liquids' },
  { value: 'pool_fire', label: 'Pool Fire', desc: '5 kW/m² radiant heat for 40s — use for flammable liquid spills' },
  { value: 'flash_fire',label: 'Flash Fire / Vapor Cloud Fire', desc: 'LFL endpoint — vapor cloud ignites' },
];

const PUBLIC_RECEPTORS = [
  { key: 'residences',     label: 'Residences' },
  { key: 'schools',        label: 'Schools' },
  { key: 'hospitals',      label: 'Hospitals / Medical Facilities' },
  { key: 'prisons',        label: 'Prisons / Detention Facilities' },
  { key: 'parks',          label: 'Public Parks / Recreational Areas' },
  { key: 'arenas',         label: 'Stadiums / Arenas' },
  { key: 'commercial',     label: 'Commercial / Industrial Areas' },
];

const ENV_RECEPTORS = [
  { key: 'national_parks',   label: 'National / State Parks or Forests' },
  { key: 'wildlife',         label: 'Wildlife Sanctuaries / Refuges' },
  { key: 'wilderness',       label: 'Federal Wilderness Areas' },
];

const inp = { width:'100%', padding:'9px 12px', borderRadius:8, border:'1px solid #E2E8F0', fontFamily:'var(--font-body)', fontSize:14, boxSizing:'border-box', background:'white' };
const lbl = { fontSize:12, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', display:'block', marginBottom:4 };
const hint = { fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)', marginTop:2 };
const grid2 = { display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:14, marginBottom:14 };

const Field = ({ label, hint: h, children }) => (
  <div>
    <label style={lbl}>{label}</label>
    {children}
    {h && <div style={hint}>{h}</div>}
  </div>
);

const SectionTitle = ({ children }) => (
  <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:12, marginTop:8, paddingBottom:6, borderBottom:'1px solid #F1F5F9' }}>{children}</div>
);

// ── Main Component ──────────────────────────────────────────
export default function RMPScreening() {
  const [step, setStep] = useState(1);
  const [facilities, setFacilities] = useState([]);
  const [chemicals, setChemicals] = useState([]);
  const [savedCalcs, setSavedCalcs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [showSaved, setShowSaved] = useState(false);
  const printRef = useRef();

  const EMPTY = {
    facility_id:'', calc_name:'',
    // Step 1 — Scenario setup
    chemical_type:'', scenario_type:'', site_type:'rural',
    // Step 2 — Chemical & quantity
    chemical_name:'', cas_number:'', quantity_lbs:'',
    process_temp_c:'25', program_level:'',
    // Step 3 — Release details
    release_type:'gas', release_scenario:'vce',
    hole_size_inches:'', hole_type:'liquid',
    tank_pressure_psig:'', static_head_ft:'10',
    is_diked:false, diked_area_ft2:'',
    building_mitigation:false, active_mitigation:false,
    yield_factor_pct:'3',
    // Step 4 — Receptors
    estimated_population:'',
    public_receptors:{}, environmental_receptors:{},
    notes:'',
  };

  const [form, setForm] = useState(EMPTY);
  const f = (u) => setForm(prev => ({ ...prev, ...u }));

  const isToxicGas = form.chemical_type === 'toxic_gas';
  const isToxicLiq = ['toxic_liquid','aqueous_solution'].includes(form.chemical_type);
  const [chemSearch, setChemSearch] = useState('');
  const [showChemDD, setShowChemDD] = useState(false);
  const [aiLooking, setAiLooking] = useState(false);
  const [aiChem, setAiChem] = useState(null);
  const [showManualChem, setShowManualChem] = useState(false);
  const [manualChem, setManualChem] = useState({ name:'', cas:'', endpoint:'', type:'toxic_liquid', A1_rural:'', A2_rural:'', A1_urban:'', A2_urban:'', B1_rural:'', B2_rural:'', B1_urban:'', B2_urban:'', LFA:'', DF:'', beta:'' });

  const aiChemLookup = async () => {
    if (!chemSearch) return;
    try {
      setAiLooking(true);
      const res = await api.post('/rmp/chemicals/lookup', { chemical_name: chemSearch, chemical_type: form.chemical_type });
      if (res.data.found) {
        setAiChem(res.data);
        // Store AI-returned constants so backend can use them for calculation
        const aiConstants = {
          name: res.data.name,
          endpoint: res.data.endpoint_mg_l,
          A1_rural: res.data.A1_rural, A2_rural: res.data.A2_rural,
          A1_urban: res.data.A1_urban, A2_urban: res.data.A2_urban,
          B1_rural: res.data.B1_rural_10min, B2_rural: res.data.B2_rural_10min,
          B1_urban: res.data.B1_urban_10min, B2_urban: res.data.B2_urban_10min,
          LFA: res.data.LFA, DF: res.data.DF, LFB: res.data.LFB,
          beta: res.data.beta,
          ai_sourced: true,
          source: res.data.source,
        };
        f({ chemical_name: res.data.name, cas_number: res.data.cas_number || '', manual_oca_constants: aiConstants });
        setChemSearch(res.data.name);
        setShowChemDD(false);
        toast.success(`Properties found for \${res.data.name} — verify against EPA OCAG before submission`);
      } else {
        toast.warn('Not found in EPA OCAG tables — enter constants manually below');
      setShowManualChem(true);
      }
    } catch(e) { toast.error('AI lookup failed'); }
    finally { setAiLooking(false); }
  };

  const isFlammable = ['flammable_gas','flammable_liquid'].includes(form.chemical_type);
  const isWorstCase = form.scenario_type === 'worst_case';
  const isAlt = form.scenario_type === 'alternative';

  useEffect(() => { loadInit(); }, []);

  const loadInit = async () => {
    try {
      const [facRes, calcRes] = await Promise.all([
        api.get('/facilities'),
        api.get('/rmp/calculations'),
      ]);
      setFacilities(facRes.data.facilities || []);
      setSavedCalcs(calcRes.data.calculations || []);
    } catch(e) { console.error(e); }
  };

  const loadChemicals = async (type) => {
    try {
      const res = await api.get(`/rmp/chemicals?type=${type}`);
      setChemicals(res.data.chemicals || []);
    } catch(e) {}
  };

  const calculate = async (save=false) => {
    try {
      setLoading(true);
      const res = await api.post('/rmp/calculate', { ...form, save_calculation: save });
      setResults(res.data);
      if (save) { toast.success('Calculation saved'); loadInit(); }
      setStep(5);
    } catch(e) {
      toast.error('Calculation failed: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  };

  const loadSaved = (calc) => {
    const inputs = calc.inputs_json || {};
    setForm({ ...EMPTY, ...inputs });
    setResults(calc.results_json);
    setStep(5);
    setShowSaved(false);
  };

  const deleteSaved = async (id) => {
    if (!window.confirm('Delete?')) return;
    await api.delete(`/rmp/calculations/${id}`);
    loadInit(); toast.success('Deleted');
  };

  const stepLabels = ['Scenario Type','Chemical & Quantity','Release Details','Receptors','Results'];

  const distanceColor = (d) => {
    if (!d) return '#374151';
    if (d > 1) return '#0066FF';
    if (d > 0.25) return '#D97706';
    return '#16A34A';
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20, flexWrap:'wrap', gap:10 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:9, marginBottom:3 }}>
            <ShieldAlert size={20} color="#DC2626" />
            <h1 style={{ fontFamily:'var(--font-heading)', fontSize:20, fontWeight:800, color:'#0A1628', margin:0 }}>RMP Screening</h1>
          </div>
          <div style={{ fontSize:12, color:'#6B7280', fontFamily:'var(--font-body)' }}>Offsite Consequence Analysis · 40 CFR Part 68 · EPA OCA Guidance · Worst-case and alternative release scenarios</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => setShowSaved(!showSaved)} style={{ display:'flex', alignItems:'center', gap:5, padding:'7px 12px', borderRadius:7, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13 }}>
            <Save size={13} /> Saved ({savedCalcs.length})
          </button>
          {step > 1 && (
            <button onClick={() => { setStep(1); setResults(null); setForm(EMPTY); }}
              style={{ padding:'7px 12px', borderRadius:7, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13 }}>
              New Calculation
            </button>
          )}
        </div>
      </div>

      {/* RMP info banner */}
      <div style={{ padding:'10px 14px', background:'#EFF6FF', border:'1px solid #FECACA', borderRadius:8, marginBottom:18, fontSize:12, color:'#1E40AF', fontFamily:'var(--font-body)' }}>
        <strong>40 CFR Part 68 — Risk Management Program:</strong> Facilities holding regulated substances above threshold quantities must conduct an Offsite Consequence Analysis (OCA). This tool implements EPA OCA Guidance equations for screening-level distance estimation. Equivalent to EPA RMP*Comp methodology.
      </div>

      {/* Saved panel */}
      {showSaved && (
        <div className="card" style={{ marginBottom:18 }}>
          <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:12 }}>Saved OCA Calculations</div>
          {savedCalcs.length === 0 ? <div style={{ textAlign:'center', color:'#94a3b8', fontFamily:'var(--font-body)', fontSize:13, padding:'16px 0' }}>No saved calculations</div> :
            savedCalcs.map(calc => (
              <div key={calc.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:'1px solid #F1F5F9' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:'#0A1628', fontFamily:'var(--font-body)' }}>{calc.calc_name}</div>
                  <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)' }}>
                    {calc.facility_name||'No facility'} · {calc.chemical_name} · {calc.scenario_type?.replace(/_/g,' ')} ·
                    <span style={{ color: distanceColor(calc.distance_to_endpoint_miles), fontWeight:700 }}> {parseFloat(calc.distance_to_endpoint_miles||0).toFixed(1)} mi</span>
                  </div>
                </div>
                <button onClick={() => loadSaved(calc)} style={{ padding:'4px 10px', borderRadius:6, border:'1px solid #DC2626', background:'#EFF6FF', color:'#0066FF', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:12, fontWeight:600 }}>Load</button>
                <button onClick={() => deleteSaved(calc.id)} style={{ background:'none', border:'none', cursor:'pointer', color:'#0066FF', padding:3 }}><Trash2 size={13} /></button>
              </div>
            ))
          }
        </div>
      )}

      {/* Step indicator */}
      {step < 5 && (
        <div style={{ display:'flex', gap:0, marginBottom:22, borderRadius:9, overflow:'hidden' }}>
          {stepLabels.slice(0,4).map((title,i) => (
            <div key={i} style={{ flex:1, padding:'9px 6px', background: step===i+1?'#0066FF': step>i+1?'#FEE2E2':'#F8FAFC', borderRight:i<3?'1px solid white':'none', textAlign:'center' }}>
              <div style={{ fontSize:9, fontWeight:700, color: step===i+1?'white': step>i+1?'#0066FF':'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', fontFamily:'var(--font-body)' }}>Step {i+1}</div>
              <div style={{ fontSize:11, fontWeight:600, color: step===i+1?'white': step>i+1?'#0066FF':'#374151', fontFamily:'var(--font-body)' }}>{title}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── STEP 1: SCENARIO TYPE ── */}
      {step === 1 && (
        <div>
          <SectionTitle>Select Chemical Type</SectionTitle>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:10, marginBottom:20 }}>
            {CHEMICAL_TYPES.map(t => (
              <div key={t.value} onClick={() => { f({ chemical_type: t.value, chemical_name:'' }); loadChemicals(t.value.includes('flammable') ? 'flammable' : t.value.includes('toxic_gas') ? 'toxic_gas' : 'toxic_liquid'); }}
                style={{ padding:14, borderRadius:10, border:`2px solid ${form.chemical_type===t.value?'#0066FF':'#E2E8F0'}`, background: form.chemical_type===t.value?'#EFF6FF':'white', cursor:'pointer' }}>
                <div style={{ fontSize:22, marginBottom:6 }}>{t.icon}</div>
                <div style={{ fontSize:13, fontWeight:700, color: form.chemical_type===t.value?'#0066FF':'#0A1628', fontFamily:'var(--font-body)', marginBottom:3 }}>{t.label}</div>
                <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)', lineHeight:1.5 }}>{t.desc}</div>
              </div>
            ))}
          </div>

          <SectionTitle>Select Scenario Type</SectionTitle>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:20 }}>
            {SCENARIO_TYPES.map(s => (
              <div key={s.value} onClick={() => f({ scenario_type: s.value })}
                style={{ padding:14, borderRadius:10, border:`2px solid ${form.scenario_type===s.value?'#0066FF':'#E2E8F0'}`, background: form.scenario_type===s.value?'#EFF6FF':'white', cursor:'pointer' }}>
                <div style={{ fontSize:13, fontWeight:700, color: form.scenario_type===s.value?'#0066FF':'#0A1628', fontFamily:'var(--font-body)', marginBottom:4 }}>{s.label}</div>
                <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)', lineHeight:1.5 }}>{s.desc}</div>
              </div>
            ))}
          </div>

          <SectionTitle>Site Type</SectionTitle>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:20 }}>
            {SITE_TYPES.map(s => (
              <div key={s.value} onClick={() => f({ site_type: s.value })}
                style={{ padding:12, borderRadius:10, border:`2px solid ${form.site_type===s.value?'#0066FF':'#E2E8F0'}`, background: form.site_type===s.value?'#EFF6FF':'white', cursor:'pointer' }}>
                <div style={{ fontSize:13, fontWeight:600, color: form.site_type===s.value?'#0066FF':'#0A1628', fontFamily:'var(--font-body)' }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={grid2}>
            <Field label="Facility (optional)">
              <select value={form.facility_id} onChange={e => f({ facility_id: e.target.value })} style={inp}>
                <option value="">Select facility</option>
                {facilities.map(fac => <option key={fac.id} value={fac.id}>{fac.facility_name||fac.name}</option>)}
              </select>
            </Field>
            <Field label="Calculation Name">
              <input type="text" value={form.calc_name} onChange={e => f({ calc_name: e.target.value })} placeholder="e.g. Chlorine WC Scenario 2026" style={inp} />
            </Field>
          </div>

          <button disabled={!form.chemical_type || !form.scenario_type} onClick={() => setStep(2)} className="btn btn-primary" style={{ background:'#0066FF', display:'flex', alignItems:'center', gap:6, marginBottom:32 }}>
            Next: Chemical & Quantity <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* ── STEP 2: CHEMICAL & QUANTITY ── */}
      {step === 2 && (
        <div>
          <SectionTitle>Chemical Selection</SectionTitle>
          <div style={{ marginBottom:14 }}>
            <Field label="Chemical Name *" hint="Must be a regulated substance under 40 CFR Part 68 §68.130">
              <input type="text" value={chemSearch}
                onChange={e => { setChemSearch(e.target.value); setShowChemDD(true); }}
                onFocus={() => setShowChemDD(true)}
                placeholder="Search 40 CFR Part 68 regulated substances..."
                style={inp} />
              {showChemDD && chemSearch && (
                <div style={{ background:'white', border:'1px solid #E2E8F0', borderRadius:8, maxHeight:220, overflowY:'auto', marginTop:4 }}>
                  {chemicals.filter(c => c.name.toLowerCase().includes(chemSearch.toLowerCase())).slice(0,25).map(c => (
                    <div key={c.name} onClick={() => { f({ chemical_name: c.name }); setChemSearch(c.name); setShowChemDD(false); }}
                      style={{ padding:'9px 14px', cursor:'pointer', borderBottom:'1px solid #F8FAFC', fontSize:13, fontFamily:'var(--font-body)' }}
                      onMouseEnter={e => e.currentTarget.style.background='#F8FAFC'}
                      onMouseLeave={e => e.currentTarget.style.background='white'}>
                      <div style={{ fontWeight:500, color:'#0A1628' }}>{c.name}</div>
                      {c.endpoint && <div style={{ fontSize:11, color:'#94a3b8' }}>Toxic endpoint: {c.endpoint} mg/L · 40 CFR Part 68 Appendix A</div>}
                    </div>
                  ))}
                  {chemicals.filter(c => c.name.toLowerCase().includes(chemSearch.toLowerCase())).length === 0 && (
                    <div style={{ padding:'12px 14px' }}>
                      <div style={{ fontSize:12, color:'#374151', fontFamily:'var(--font-body)', fontWeight:600, marginBottom:4 }}>Not found in regulated substances list</div>
                      <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)', marginBottom:8 }}>
                        Verify this chemical is listed in 40 CFR §68.130 before proceeding. If it is regulated and missing from our database, enter the OCA constants manually from EPA OCAG Appendix B.
                      </div>
                      <button onClick={() => { setShowManualChem(true); setShowChemDD(false); }}
                        style={{ fontSize:11, fontWeight:600, color:'#0066FF', background:'none', border:'1px solid #0066FF', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontFamily:'var(--font-body)' }}>
                        Enter OCA constants manually
                      </button>
                    </div>
                  )}
                </div>
              )}
            </Field>
            <div style={{ marginTop:8 }}>
              <Field label="CAS Number (optional)">
                <input type="text" value={form.cas_number} onChange={e => f({ cas_number: e.target.value })} placeholder="e.g. 7782-50-5" style={{ ...inp, maxWidth:220 }} />
              </Field>
            </div>
          </div>

          {showManualChem && (
            <div style={{ padding:'14px 16px', background:'#F8FAFC', border:'1px solid #0066FF', borderRadius:8, marginBottom:14 }}>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:13, fontWeight:700, color:'#0066FF', marginBottom:10 }}>
                Manual OCA Constants Entry
              </div>
              <div style={{ fontSize:11, color:'#94a3b8', fontFamily:'var(--font-body)', marginBottom:10, fontStyle:'italic' }}>
                Enter constants from EPA OCAG Appendix B. These values must be verified against published EPA tables before use in an RMP submission.
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                <div>
                  <label style={{ fontSize:11, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', display:'block', marginBottom:3 }}>Chemical Name *</label>
                  <input type="text" value={manualChem.name} onChange={e => setManualChem(m => ({...m, name:e.target.value}))} style={inp} />
                </div>
                <div>
                  <label style={{ fontSize:11, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', display:'block', marginBottom:3 }}>Toxic Endpoint (mg/L)</label>
                  <input type="number" value={manualChem.endpoint} onChange={e => setManualChem(m => ({...m, endpoint:e.target.value}))} placeholder="e.g. 0.016" style={inp} />
                </div>
              </div>
              {(form.chemical_type === 'toxic_gas' || form.chemical_type === 'toxic_liquid_gas') && (
                <>
                  <div style={{ fontSize:11, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', marginBottom:6 }}>Worst-Case Constants (Eq. 1: D = A1 × QR^A2)</div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:10 }}>
                    {[['A1 Rural','A1_rural'],['A2 Rural','A2_rural'],['A1 Urban','A1_urban'],['A2 Urban','A2_urban']].map(([label,key]) => (
                      <div key={key}>
                        <label style={{ fontSize:10, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', display:'block', marginBottom:2 }}>{label}</label>
                        <input type="number" value={manualChem[key]} onChange={e => setManualChem(m => ({...m, [key]:e.target.value}))} style={{ ...inp, fontSize:12 }} />
                      </div>
                    ))}
                  </div>
                </>
              )}
              {['toxic_liquid','aqueous_solution'].includes(form.chemical_type) && (
                <>
                  <div style={{ fontSize:11, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', marginBottom:6 }}>Worst-Case Constants (Eq. 8: D = B1 × QR^B2)</div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:8, marginBottom:10 }}>
                    {[['B1 Rural 10min','B1_rural'],['B2 Rural 10min','B2_rural'],['B1 Urban 10min','B1_urban'],['B2 Urban 10min','B2_urban']].map(([label,key]) => (
                      <div key={key}>
                        <label style={{ fontSize:10, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', display:'block', marginBottom:2 }}>{label}</label>
                        <input type="number" value={manualChem[key]} onChange={e => setManualChem(m => ({...m, [key]:e.target.value}))} style={{ ...inp, fontSize:12 }} />
                      </div>
                    ))}
                  </div>
                  <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:10 }}>
                    {[['LFA (Liquid Factor Ambient)','LFA'],['DF (Density Factor)','DF'],['LFB (Liquid Factor Boiling)','LFB']].map(([label,key]) => (
                      <div key={key}>
                        <label style={{ fontSize:10, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', display:'block', marginBottom:2 }}>{label}</label>
                        <input type="number" value={manualChem[key]||''} onChange={e => setManualChem(m => ({...m, [key]:e.target.value}))} style={{ ...inp, fontSize:12 }} />
                      </div>
                    ))}
                  </div>
                </>
              )}
              {['flammable_gas','flammable_liquid'].includes(form.chemical_type) && (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:10 }}>
                  <div>
                    <label style={{ fontSize:10, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', display:'block', marginBottom:2 }}>Beta Factor (β) for TNT equivalency</label>
                    <input type="number" value={manualChem.beta} onChange={e => setManualChem(m => ({...m, beta:e.target.value}))} placeholder="e.g. 0.0080" style={inp} />
                  </div>
                </div>
              )}
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => {
                  if (!manualChem.name) return;
                  f({ chemical_name: manualChem.name, cas_number: manualChem.cas || '', manual_oca_constants: manualChem });
                  setChemSearch(manualChem.name);
                  setShowManualChem(false);
                  setAiChem({ manual: true });
                }} className="btn btn-primary" style={{ fontSize:12 }}>Use These Constants</button>
                <button onClick={() => setShowManualChem(false)} style={{ padding:'6px 12px', borderRadius:7, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:12 }}>Cancel</button>
              </div>
            </div>
          )}

          <SectionTitle>Release Quantity</SectionTitle>
          <div style={grid2}>
            <Field label="Quantity in Largest Single Vessel or Pipeline (lb) *" hint="For worst-case: greatest amount in single vessel. For alternative: release quantity for your scenario.">
              <input type="number" value={form.quantity_lbs} onChange={e => f({ quantity_lbs: e.target.value })} placeholder="e.g. 10000" style={inp} />
            </Field>
            <Field label="Process / Storage Temperature (°C)" hint="Used for temperature correction of evaporation rates. Default: 25°C ambient.">
              <input type="number" value={form.process_temp_c} onChange={e => f({ process_temp_c: e.target.value })} style={inp} />
            </Field>
          </div>

          <SectionTitle>RMP Program Level</SectionTitle>
          <div style={{ marginBottom:14 }}>
            <Field label="Program Level" hint="Determines reporting requirements. Program 1 = no public receptors within endpoint. Program 2/3 = most facilities.">
              <select value={form.program_level} onChange={e => f({ program_level: e.target.value })} style={{ ...inp, maxWidth:300 }}>
                <option value="">Not determined yet</option>
                <option value="1">Program 1 — No public receptors within endpoint distance</option>
                <option value="2">Program 2 — NAICS-specific, not Program 1 or 3</option>
                <option value="3">Program 3 — OSHA PSM-covered process or NAICS code specified in §68.10</option>
              </select>
            </Field>
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={() => setStep(1)} style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', borderRadius:8, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:14 }}><ChevronLeft size={14} /> Back</button>
            <button disabled={!form.chemical_name || !form.quantity_lbs} onClick={() => setStep(3)} className="btn btn-primary" style={{ background:'#0066FF', display:'flex', alignItems:'center', gap:6 }}>
              Next: Release Details <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: RELEASE DETAILS ── */}
      {step === 3 && (
        <div>
          {/* Toxic Gas release details */}
          {isToxicGas && (
            <>
              <SectionTitle>Worst-Case: Toxic Gas Release</SectionTitle>
              {isWorstCase && (
                <div style={{ padding:'12px 14px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8, marginBottom:14, fontSize:12, color:'#1E40AF', fontFamily:'var(--font-body)' }}>
                  <strong>EPA OCA Guidance — Worst-Case Toxic Gas:</strong> The entire contents of the largest vessel are assumed to be released over 10 minutes at ground level. Wind speed: 1.5 m/s, F stability class. Distance calculated using Equation 1: D = A1 × QR^A2.
                </div>
              )}
              {isAlt && (
                <>
                  <div style={{ padding:'12px 14px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, marginBottom:14, fontSize:12, color:'#92400E', fontFamily:'var(--font-body)' }}>
                    <strong>Alternative Scenario:</strong> Choose the release type — liquid release from pressurized vessel (Eq. 11) or vapor release from hole in vapor space (Eq. 12). Wind: 3 m/s, D stability.
                  </div>
                  <div style={grid2}>
                    <Field label="Release Type">
                      <select value={form.release_type} onChange={e => f({ release_type: e.target.value })} style={inp}>
                        <option value="liquid_pressurized">Liquid release from pressurized vessel (Eq. 11)</option>
                        <option value="vapor_pressurized">Vapor release from hole in vapor space (Eq. 12)</option>
                      </select>
                    </Field>
                    <Field label="Hole / Puncture Diameter (inches)" hint="e.g. 0.25 for 1/4-inch hole, 1.0 for 1-inch pipe rupture">
                      <input type="number" value={form.hole_size_inches} onChange={e => f({ hole_size_inches: e.target.value })} placeholder="e.g. 0.25" style={inp} />
                    </Field>
                  </div>
                </>
              )}
            </>
          )}

          {/* Toxic Liquid release details */}
          {isToxicLiq && (
            <>
              <SectionTitle>{isWorstCase ? 'Worst-Case:' : 'Alternative:'} Toxic Liquid Spill</SectionTitle>
              {isWorstCase && (
                <div style={{ padding:'12px 14px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8, marginBottom:14, fontSize:12, color:'#1E40AF', fontFamily:'var(--font-body)' }}>
                  <strong>EPA OCA Guidance — Worst-Case Toxic Liquid:</strong> Total contents of largest vessel spilled instantaneously. Pool spreads to 1 cm depth (undiked) or fills dike area. Evaporation rate per Eq. 2 or 6. Distance per Eq. 8.
                </div>
              )}
              {isAlt && (
                <div style={{ padding:'12px 14px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, marginBottom:14, fontSize:12, color:'#92400E', fontFamily:'var(--font-body)' }}>
                  <strong>Alternative Scenario:</strong> Liquid release rate from Eq. 16 (atmospheric tank). Evaporation rate at 3 m/s wind. Distance per Eq. 20.
                </div>
              )}
              <div style={grid2}>
                <Field label="Containment">
                  <select value={form.is_diked} onChange={e => f({ is_diked: e.target.value === 'true' })} style={inp}>
                    <option value="false">Undiked area — pool spreads to 1 cm depth</option>
                    <option value="true">Diked / Contained area</option>
                  </select>
                </Field>
                {form.is_diked && (
                  <Field label="Diked Area (ft²)" hint="Area within the containment dike">
                    <input type="number" value={form.diked_area_ft2} onChange={e => f({ diked_area_ft2: e.target.value })} style={inp} />
                  </Field>
                )}
                {isAlt && (
                  <>
                    <Field label="Hole / Leak Diameter (inches)" hint="For liquid release rate calculation (Eq. 16)">
                      <input type="number" value={form.hole_size_inches} onChange={e => f({ hole_size_inches: e.target.value })} placeholder="e.g. 0.25" style={inp} />
                    </Field>
                    <Field label="Static Head (ft)" hint="Height of liquid above the leak point">
                      <input type="number" value={form.static_head_ft} onChange={e => f({ static_head_ft: e.target.value })} placeholder="10" style={inp} />
                    </Field>
                  </>
                )}
              </div>
            </>
          )}

          {/* Flammable details */}
          {isFlammable && (
            <>
              <SectionTitle>Flammable Release Scenario</SectionTitle>
              <div style={{ marginBottom:14 }}>
                <Field label="Release Scenario">
                  <div style={{ display:'grid', gridTemplateColumns:'1fr', gap:8 }}>
                    {RELEASE_SCENARIOS.filter(s => form.chemical_type === 'flammable_gas' ? s.value !== 'pool_fire' : s.value !== 'flash_fire').map(s => (
                      <div key={s.value} onClick={() => f({ release_scenario: s.value })}
                        style={{ padding:12, borderRadius:8, border:`2px solid ${form.release_scenario===s.value?'#0066FF':'#E2E8F0'}`, background: form.release_scenario===s.value?'#EFF6FF':'white', cursor:'pointer' }}>
                        <div style={{ fontSize:13, fontWeight:600, color: form.release_scenario===s.value?'#0066FF':'#0A1628', fontFamily:'var(--font-body)', marginBottom:2 }}>{s.label}</div>
                        <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)' }}>{s.desc}</div>
                      </div>
                    ))}
                  </div>
                </Field>
              </div>
              {isAlt && form.release_scenario === 'vce' && (
                <div style={grid2}>
                  <Field label="Yield Factor (%)" hint="EPA default worst-case: 10%. More likely alternative: 3%.">
                    <select value={form.yield_factor_pct} onChange={e => f({ yield_factor_pct: e.target.value })} style={inp}>
                      <option value="10">10% — Worst-case (regulatory default)</option>
                      <option value="3">3% — Alternative (more representative)</option>
                      <option value="1">1% — Low yield</option>
                    </select>
                  </Field>
                </div>
              )}
              {form.release_scenario === 'pool_fire' && (
                <div style={grid2}>
                  <Field label="Pool / Diked Area (ft²)" hint="Area of flammable liquid pool">
                    <input type="number" value={form.diked_area_ft2} onChange={e => f({ diked_area_ft2: e.target.value })} style={inp} />
                  </Field>
                </div>
              )}
            </>
          )}

          <SectionTitle>Mitigation</SectionTitle>
          <div style={grid2}>
            <Field label="Passive Mitigation — Building" hint={isToxicGas ? "55% reduction factor for toxic gases in enclosed buildings (OCAG Appendix D)" : "10% reduction factor for worst-case; 5% for alternative scenarios (toxic liquids)"}>
              <select value={form.building_mitigation} onChange={e => f({ building_mitigation: e.target.value === 'true' })} style={inp}>
                <option value="false">No building mitigation</option>
                <option value="true">Yes — release is inside a fully enclosed building</option>
              </select>
            </Field>
            {isAlt && (
              <Field label="Active Mitigation (Alternative Only)" hint="Interlocks, deluge systems, isolation valves — allowed for alternative scenarios only">
                <select value={form.active_mitigation} onChange={e => f({ active_mitigation: e.target.value === 'true' })} style={inp}>
                  <option value="false">No active mitigation</option>
                  <option value="true">Yes — active mitigation systems in place</option>
                </select>
              </Field>
            )}
          </div>

          <div style={{ display:'flex', gap:10 }}>
            <button onClick={() => setStep(2)} style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', borderRadius:8, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:14 }}><ChevronLeft size={14} /> Back</button>
            <button onClick={() => setStep(4)} className="btn btn-primary" style={{ background:'#0066FF', display:'flex', alignItems:'center', gap:6 }}>
              Next: Receptors <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 4: RECEPTORS ── */}
      {step === 4 && (
        <div>
          <SectionTitle>Offsite Receptors (40 CFR §68.165)</SectionTitle>
          <div style={{ padding:'10px 14px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8, marginBottom:14, fontSize:12, color:'#1E40AF', fontFamily:'var(--font-body)' }}>
            Required for RMP submission: identify population and receptor types within the distance-to-endpoint circle. Calculate distance first, then identify receptors on local maps.
          </div>

          <div style={grid2}>
            <Field label="Estimated Residential Population within Endpoint Circle" hint="Use Census data. Round to 2 significant figures (e.g., 1260 → 1300). If under 10, use actual number.">
              <input type="number" value={form.estimated_population} onChange={e => f({ estimated_population: e.target.value })} placeholder="e.g. 2500" style={inp} />
            </Field>
          </div>

          <SectionTitle>Public Receptors within Endpoint Circle</SectionTitle>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8, marginBottom:14 }}>
            {PUBLIC_RECEPTORS.map(r => (
              <label key={r.key} style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 12px', background: form.public_receptors[r.key]?'#EFF6FF':'#F8FAFC', border:`1px solid ${form.public_receptors[r.key]?'#BFDBFE':'#E2E8F0'}`, borderRadius:7, cursor:'pointer', fontSize:13, fontFamily:'var(--font-body)', color:'#374151' }}>
                <input type="checkbox" checked={!!form.public_receptors[r.key]}
                  onChange={e => f({ public_receptors: { ...form.public_receptors, [r.key]: e.target.checked }})}
                  style={{ width:14, height:14 }} />
                {r.label}
              </label>
            ))}
          </div>

          <SectionTitle>Environmental Receptors within Endpoint Circle</SectionTitle>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8, marginBottom:14 }}>
            {ENV_RECEPTORS.map(r => (
              <label key={r.key} style={{ display:'flex', alignItems:'center', gap:8, padding:'9px 12px', background: form.environmental_receptors[r.key]?'#F0FDF4':'#F8FAFC', border:`1px solid ${form.environmental_receptors[r.key]?'#BBF7D0':'#E2E8F0'}`, borderRadius:7, cursor:'pointer', fontSize:13, fontFamily:'var(--font-body)', color:'#374151' }}>
                <input type="checkbox" checked={!!form.environmental_receptors[r.key]}
                  onChange={e => f({ environmental_receptors: { ...form.environmental_receptors, [r.key]: e.target.checked }})}
                  style={{ width:14, height:14 }} />
                {r.label}
              </label>
            ))}
          </div>

          <Field label="Notes / Scenario Description" hint="Document your scenario assumptions for RMP recordkeeping (40 CFR §68.165)">
            <textarea value={form.notes} onChange={e => f({ notes: e.target.value })} rows={3}
              placeholder="Document: vessel selected, assumptions, administrative controls, mitigation systems assumed..." style={{ ...inp, resize:'vertical' }} />
          </Field>

          <div style={{ display:'flex', gap:10, marginTop:14, flexWrap:'wrap' }}>
            <button onClick={() => setStep(3)} style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', borderRadius:8, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:14 }}><ChevronLeft size={14} /> Back</button>
            <button onClick={() => calculate(false)} disabled={loading} className="btn btn-primary" style={{ background:'#0066FF', display:'flex', alignItems:'center', gap:6 }}>
              <Calculator size={14} /> {loading ? 'Calculating...' : 'Calculate Distance'}
            </button>
            <button onClick={() => calculate(true)} disabled={loading}
              style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 16px', borderRadius:8, border:'1px solid #16A34A', background:'#F0FDF4', color:'#16A34A', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:14, fontWeight:600 }}>
              <Save size={14} /> Calculate & Save
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 5: RESULTS ── */}
      {step === 5 && results && (
        <div ref={printRef}>
          <style>{`@media print { .no-print{display:none!important;} body{background:white!important;} }`}</style>

          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }} className="no-print">
            <div style={{ fontFamily:'var(--font-heading)', fontSize:16, fontWeight:700, color:'#0A1628' }}>OCA Results</div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => window.print()} style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 12px', borderRadius:8, border:'1px solid #E2E8F0', background:'white', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13 }}>
                <Printer size={13} /> Print / PDF
              </button>
              <button onClick={() => calculate(true)} style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 12px', borderRadius:8, border:'1px solid #16A34A', background:'#F0FDF4', color:'#16A34A', cursor:'pointer', fontFamily:'var(--font-body)', fontSize:13, fontWeight:600 }}>
                <Save size={13} /> Save
              </button>
            </div>
          </div>

          {/* Report header */}
          <div className="card" style={{ padding:20, marginBottom:14 }}>
            <div style={{ textAlign:'center', marginBottom:16, paddingBottom:14, borderBottom:'2px solid #0066FF' }}>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:18, fontWeight:800, color:'#0066FF', marginBottom:3 }}>AirComply — RMP Offsite Consequence Analysis</div>
              <div style={{ fontSize:12, color:'#6B7280', fontFamily:'var(--font-body)' }}>
                40 CFR Part 68 · EPA OCA Guidance (1999) · Equivalent to RMP*Comp · {new Date().toLocaleDateString()}
              </div>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8 }}>
              {[
                { label:'Calculation Name', value: form.calc_name || `${form.chemical_name} ${form.scenario_type?.replace(/_/g,' ')}` },
                { label:'Facility', value: facilities.find(f=>f.id==form.facility_id)?.facility_name || 'Not specified' },
                { label:'Chemical', value: form.chemical_name },
                { label:'Scenario Type', value: form.scenario_type === 'worst_case' ? 'Worst-Case Release Scenario' : 'Alternative Release Scenario' },
                { label:'Site Type', value: form.site_type === 'rural' ? 'Rural (flat terrain)' : 'Urban (obstructed terrain)' },
                { label:'RMP Program Level', value: form.program_level ? `Program ${form.program_level}` : 'Not specified' },
                { label:'Quantity Released', value: `${parseFloat(form.quantity_lbs||0).toLocaleString()} lb` },
                { label:'Calculation Date', value: new Date().toLocaleDateString() },
              ].map((item,i) => (
                <div key={i} style={{ padding:'7px 10px', background:'#F8FAFC', borderRadius:7 }}>
                  <div style={{ fontSize:9, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.5px', fontFamily:'var(--font-body)', marginBottom:2 }}>{item.label}</div>
                  <div style={{ fontSize:12, fontWeight:600, color:'#0A1628', fontFamily:'var(--font-body)' }}>{item.value||'—'}</div>
                </div>
              ))}
            </div>
          </div>

          {/* KEY RESULT */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:14 }}>
            <div style={{ gridColumn:'1/-1', padding:'20px', background: parseFloat(results.distance_miles||0)>1?'#EFF6FF':'#F0FDF4', border:`2px solid ${parseFloat(results.distance_miles||0)>1?'#BFDBFE':'#BBF7D0'}`, borderRadius:10, textAlign:'center' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.5px', fontFamily:'var(--font-body)', marginBottom:6 }}>
                Distance to {results.endpoint_type || 'Toxic Endpoint'}
              </div>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:48, fontWeight:800, color: distanceColor(results.distance_miles), marginBottom:4 }}>
                {parseFloat(results.distance_miles||0).toFixed(1)}
              </div>
              <div style={{ fontSize:16, fontWeight:600, color: distanceColor(results.distance_miles), fontFamily:'var(--font-body)' }}>miles</div>
              {results.endpoint_mg_l && (
                <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)', marginTop:6 }}>
                  Toxic endpoint: {results.endpoint_mg_l} mg/L (ERPG-2 or equivalent)
                </div>
              )}
            </div>
            <div style={{ padding:'14px', background:'#F8FAFC', border:'1px solid #E2E8F0', borderRadius:9 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', fontFamily:'var(--font-body)', marginBottom:4 }}>Release Rate</div>
              <div style={{ fontSize:18, fontWeight:800, color:'#0A1628', fontFamily:'var(--font-heading)' }}>
                {results.release_rate_lb_min ? parseFloat(results.release_rate_lb_min).toFixed(1) : '—'}
              </div>
              <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)' }}>lb/min</div>
            </div>
            <div style={{ padding:'14px', background:'#F8FAFC', border:'1px solid #E2E8F0', borderRadius:9 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', fontFamily:'var(--font-body)', marginBottom:4 }}>Release Duration</div>
              <div style={{ fontSize:18, fontWeight:800, color:'#0A1628', fontFamily:'var(--font-heading)' }}>
                {results.release_duration_min ? `${parseFloat(results.release_duration_min).toFixed(0)}` : form.scenario_type==='worst_case' && isToxicGas ? '10' : '—'}
              </div>
              <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)' }}>minutes</div>
            </div>
            <div style={{ padding:'14px', background:'#F8FAFC', border:'1px solid #E2E8F0', borderRadius:9 }}>
              <div style={{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', fontFamily:'var(--font-body)', marginBottom:4 }}>Wind / Stability</div>
              <div style={{ fontSize:14, fontWeight:700, color:'#0A1628', fontFamily:'var(--font-body)' }}>{results.wind_speed || '—'}</div>
              <div style={{ fontSize:11, color:'#6B7280', fontFamily:'var(--font-body)' }}>{results.stability || ''} stability class</div>
            </div>
          </div>

          {/* Intermediate calculations */}
          {Object.keys(results).filter(k => !['distance_miles','release_rate_lb_min','release_duration_min','wind_speed','stability','endpoint_mg_l','endpoint_type','inputs','method'].includes(k) && results[k] !== undefined && results[k] !== null).length > 0 && (
            <div className="card" style={{ padding:'18px 20px', marginBottom:14 }}>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:12 }}>Intermediate Calculation Values</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
                {Object.entries(results).filter(([k]) => !['distance_miles','endpoint_mg_l','endpoint_type','inputs','wind_speed','stability'].includes(k)).map(([key,val],i) => (
                  <div key={i} style={{ padding:'8px 10px', background:'#F8FAFC', borderRadius:7 }}>
                    <div style={{ fontSize:9, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.4px', fontFamily:'var(--font-body)', marginBottom:2 }}>{key.replace(/_/g,' ')}</div>
                    <div style={{ fontSize:12, fontWeight:600, color:'#0A1628', fontFamily:'var(--font-body)' }}>{typeof val === 'number' ? val.toFixed(4) : String(val)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Method reference */}
          <div style={{ padding:'10px 14px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8, marginBottom:12 }}>
            <div style={{ fontSize:11, color:'#1E40AF', fontFamily:'var(--font-body)', lineHeight:1.7 }}>
              <strong>Calculation Method:</strong> {results.method}
            </div>
          </div>

          {/* Receptors */}
          {(form.estimated_population || Object.keys(form.public_receptors||{}).filter(k=>form.public_receptors[k]).length > 0) && (
            <div className="card" style={{ padding:'18px 20px', marginBottom:14 }}>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:12 }}>Offsite Receptors within {parseFloat(results.distance_miles||0).toFixed(1)} mile endpoint circle</div>
              {form.estimated_population && <div style={{ fontSize:13, fontFamily:'var(--font-body)', color:'#374151', marginBottom:8 }}><strong>Estimated residential population:</strong> {parseInt(form.estimated_population||0).toLocaleString()}</div>}
              {Object.keys(form.public_receptors||{}).filter(k=>form.public_receptors[k]).length > 0 && (
                <div style={{ marginBottom:6 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:'#374151', fontFamily:'var(--font-body)', marginBottom:4 }}>Public receptors present:</div>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                    {PUBLIC_RECEPTORS.filter(r=>form.public_receptors[r.key]).map(r => (
                      <span key={r.key} style={{ fontSize:11, fontWeight:600, background:'#EFF6FF', color:'#1E40AF', padding:'2px 8px', borderRadius:20, fontFamily:'var(--font-body)' }}>{r.label}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          {form.notes && (
            <div className="card" style={{ padding:'18px 20px', marginBottom:14 }}>
              <div style={{ fontFamily:'var(--font-heading)', fontSize:14, fontWeight:700, color:'#0A1628', marginBottom:8 }}>Scenario Documentation (40 CFR §68.165)</div>
              <div style={{ fontSize:13, color:'#374151', fontFamily:'var(--font-body)', lineHeight:1.7, whiteSpace:'pre-wrap' }}>{form.notes}</div>
            </div>
          )}

          {/* Disclaimer */}
          <div style={{ padding:'12px 16px', background:'#FFFBEB', border:'1px solid #FDE68A', borderRadius:8, marginBottom:12 }}>
            <div style={{ fontSize:11, color:'#92400E', fontFamily:'var(--font-body)', lineHeight:1.7 }}>
              <strong>Disclaimer:</strong> Distance estimates are calculated using EPA Offsite Consequence Analysis Guidance equations (1999) and are equivalent to results from EPA RMP*Comp. Results are screening-level estimates only and may be conservative. Users are not required to use this methodology — alternative air dispersion models may be used subject to 40 CFR §68.22 requirements. Results should be reviewed by a qualified professional before use in a Risk Management Plan (RMP) submission to EPA. This tool does not constitute regulatory approval of any scenario.
            </div>
          </div>

          <div style={{ padding:'12px 16px', background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8 }}>
            <div style={{ fontSize:11, color:'#1E40AF', fontFamily:'var(--font-body)', lineHeight:1.7 }}>
              <strong>Regulatory Reference:</strong> {results.regulatory_citation || '40 CFR Part 68 — Chemical Accident Prevention Provisions. EPA Offsite Consequence Analysis Guidance (April 1999)'}. Endpoint distances correspond to ERPG-2 or equivalent concentrations from 40 CFR Part 68 Appendix A. RMPs must be submitted to EPA via RMP*eSubmit at cdx.epa.gov.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
